import { useEffect, useState } from 'react';
import type { OrderChangeRequest, OrderDetail } from '@/api/types';
import { ApiError } from '@/api/client';
import { Icon } from '@/components/Icon';
import { Tag } from '@/components/Tag';
import { dateTime, qty } from '@/lib/format';
import { useFormFactor } from '@/lib/formFactor';
import { orderStatus } from '@/lib/status';
import { reviewOrderChange } from './api';
import s from './OrderChangeReviewDialog.module.css';

const READONLY = import.meta.env.VITE_READONLY !== '0';

function diffRows(value: unknown) {
  if (!Array.isArray(value)) return [];
  type DiffRow = { key: string; label: string; before: string; after: string };
  type BundleDiffRow = { key: string; label: string; before: Array<{ product: string; quantity: string; metadata: string }>; after: Array<{ product: string; quantity: string; metadata: string }> };
  const rows = value.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') return [];
    const row = entry as Record<string, unknown>;
    return [{ key: String(row.key || row.label || ''), label: String(row.label || row.key || '变更'), before: String(row.before ?? '—'), after: String(row.after ?? '—') }];
  });
  const total = rows.find((row) => row.key === 'totalAmount');
  const visible = rows.filter((row) => !(['productAmount', 'balanceAmount'].includes(row.key) && total && row.before === total.before && row.after === total.after));
  const grouped = new Map<string, BundleDiffRow>();
  const output: Array<DiffRow | BundleDiffRow> = [];
  for (const row of visible) {
    const bundle = row.key.startsWith('line:') ? row.label.match(/^套装\s+(.+?)\s\/\s/) : null;
    if (!bundle) { output.push(row); continue; }
    const bundleName = bundle[1];
    const groupKey = `bundle:${bundleName}`;
    let group = grouped.get(groupKey);
    if (!group) {
      group = { key: groupKey, label: `套装 ${bundleName}`, before: [], after: [] };
      grouped.set(groupKey, group);
      output.push(group);
    }
    const product = row.label.slice(bundle[0].length).replace(/^\s*[A-Z0-9-]+\s+/, '').trim();
    const concise = (text: string) => {
      const parts = text.split('，');
      const quantity = parts.shift() || text;
      return { quantity, metadata: parts.join('，') };
    };
    const before = concise(row.before);
    const after = concise(row.after);
    const metadataChanged = before.metadata !== after.metadata;
    group.before.push({ product, quantity: before.quantity, metadata: metadataChanged ? before.metadata : '' });
    group.after.push({ product, quantity: after.quantity, metadata: metadataChanged ? after.metadata : '' });
  }
  const summarizeBundle = (items: BundleDiffRow['before']) => {
    const primary = items.find((item) => !/外箱|纸箱|包装/.test(item.product)) || items[0];
    if (!primary) return '—';
    const exceptions = items.filter((item) => item !== primary && !/外箱|纸箱|包装/.test(item.product) && item.quantity !== primary.quantity);
    const metadataChanges = items.filter((item) => item.metadata);
    const details = [...exceptions.map((item) => `${item.product} ${item.quantity}`), ...metadataChanges.map((item) => `${item.product}：${item.metadata}`)];
    return `${primary.quantity} 套${details.length ? `（${details.join('；')}）` : ''}`;
  };
  return output.reduce<DiffRow[]>((normalized, row) => {
    if (row.key.startsWith('bundle:')) {
      const bundle = row as BundleDiffRow;
      normalized.push({ key: bundle.key, label: bundle.label, before: summarizeBundle(bundle.before), after: summarizeBundle(bundle.after) });
    } else normalized.push(row as DiffRow);
    return normalized;
  }, []);
}

export function OrderChangeReviewDialog({ order, request, canReview, onClose, onSuccess }: { order: OrderDetail; request: OrderChangeRequest; canReview: boolean; onClose: () => void; onSuccess: () => void }) {
  const formFactor = useFormFactor();
  const [reviewNote, setReviewNote] = useState(request.reviewNote || '');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<'APPROVE' | 'REJECT' | null>(null);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState('');
  const rows = diffRows(request.diff).sort((a, b) => {
    const rank = (row: { key: string; label: string }) => /交货期|交期|delivery/i.test(`${row.key} ${row.label}`) ? 0 : row.label.startsWith('套装 ') ? 1 : /金额|amount/i.test(`${row.key} ${row.label}`) ? 2 : 3;
    return rank(a) - rank(b);
  });
  const impact = (row: { key: string; label: string; before: string; after: string }) => {
    if (/交货期|交期|delivery/i.test(`${row.key} ${row.label}`)) {
      const before = Date.parse(row.before);
      const after = Date.parse(row.after);
      if (Number.isFinite(before) && Number.isFinite(after)) {
        const days = Math.round((after - before) / 86_400_000);
        return days > 0 ? `顺延 ${days} 天` : days < 0 ? `提前 ${Math.abs(days)} 天` : '日期未变';
      }
    }
    if (/金额|amount/i.test(`${row.key} ${row.label}`)) {
      const before = Number(row.before.replace(/[^\d.-]/g, ''));
      const after = Number(row.after.replace(/[^\d.-]/g, ''));
      if (Number.isFinite(before) && Number.isFinite(after)) {
        const delta = after - before;
        return `${delta >= 0 ? '+' : '−'} ¥${Math.abs(delta).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`;
      }
    }
    return row.label.startsWith('套装 ') ? '按新套数重新核对库存' : '—';
  };
  const close = () => { if (result) onSuccess(); onClose(); };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape' && !saving) close(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, onSuccess, result, saving]);

  const submit = async (action: 'APPROVE' | 'REJECT') => {
    if (READONLY || !canReview || request.status !== 'PENDING' || !rows.length || saving || uncertain || action === 'REJECT' && !reviewNote.trim()) return;
    setSaving(true);
    setError('');
    try {
      await reviewOrderChange(order.id, request.id, action, reviewNote.trim());
      setResult(action);
    } catch (cause) {
      if (!(cause instanceof ApiError) || cause.status === 0 || cause.status >= 500) setUncertain(true);
      setError(cause instanceof Error ? cause.message : '审核失败');
    } finally { setSaving(false); }
  };

  return <div className={s.backdrop} onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) close(); }}>
    <section className={`${s.dialog} ${formFactor === 'tablet' ? s.tablet : ''}`} role="dialog" aria-modal="true" aria-label="审核变更申请">
      <header className={s.head}><div><b>审核变更申请</b><small>{order.orderNo}</small></div><button type="button" onClick={close} disabled={saving} aria-label="关闭"><Icon name="x" size={20} /></button></header>
      {result ? <div className={s.done}><Icon name="check" size={24} /><b>{result === 'APPROVE' ? '变更已通过' : '变更已驳回'}</b><p>{result === 'APPROVE' ? '共享订单已更新，跟单员确认知晓前，组装与发货暂停。' : '审核结果已写入 V1 共用变更记录。'}</p><button type="button" onClick={close}>完成</button></div> : <>
        <div className={s.body}>
          <div className={s.group}>
            <div className={s.kv}><span>申请人</span><b>{request.requester?.name || '—'} · {dateTime(request.createdAt)}</b></div>
            <div className={s.kv}><span>申请原因</span><b>{request.reason || '未填写'}</b></div>
            <div className={s.kv}><span>当前状态</span><b><Tag tone={orderStatus[order.status]?.tone || 'none'} sm>{orderStatus[order.status]?.label || order.status}</Tag>{order.assemblyLineNameSnapshot ? ` · ${order.assemblyLineNameSnapshot}` : ''}</b></div>
          </div>
          <div className={s.groupTitle}>变更内容</div>
          <div className={s.group}>{rows.length ? formFactor === 'tablet' ? <div className={s.tableWrap}><table className={s.impactTable}><thead><tr><th>字段</th><th>原值</th><th>新值</th><th>影响</th></tr></thead><tbody>{rows.map((row, index) => <tr key={`${row.key}-${index}`}><td>{row.label}</td><td><del>{row.before}</del></td><td><b>{row.after}</b></td><td className={row.label.startsWith('套装 ') ? s.rowImpact : ''}>{impact(row)}</td></tr>)}</tbody></table></div> : <ul className={s.diffs}>{rows.map((row, index) => <li key={`${row.key}-${index}`}><span>{row.label}</span><div><del>{row.before}</del><span aria-hidden="true">→</span><b>{row.after}</b></div></li>)}</ul> : <p className={s.empty}>当前接口未返回可核对的变更差异，不能提交审核。</p>}</div>
          <div className={s.warning}><Icon name="alert" size={18} /><div><strong>通过后会暂停组装与发货</strong><p>{formFactor === 'tablet' ? <>要等跟单员{order.followerUser?.name || ''}确认已知晓才恢复；库存按新订单核对{order.stockStatus?.shortageQuantity ? `（当前缺口 ${qty(order.stockStatus.shortageQuantity)} 个）` : ''}。</> : <>共享订单变更生效后，需当前跟单员确认已知晓，履约才能恢复。库存将按变更后的订单重新核对{order.stockStatus?.shortageQuantity ? `；当前缺口 ${qty(order.stockStatus.shortageQuantity)} 个` : ''}。</>}</p></div></div>
          <div className={s.group}><label>审核意见<textarea rows={3} value={reviewNote} onChange={(event) => setReviewNote(event.target.value)} placeholder="驳回时必须填写原因" /></label></div>
          {READONLY && <p className={s.hint}>当前为生产只读模式，可查看申请，暂不能审核。</p>}
          {!canReview && <p className={s.hint}>当前账号没有变更审核权限。</p>}
          {error && <p className={s.error} role="alert">{error}{uncertain && '。结果未确认，请先核对订单和变更记录，暂勿重复提交。'}</p>}
        </div>
        <footer className={s.footer}><button type="button" className={s.reject} disabled={READONLY || !canReview || request.status !== 'PENDING' || !rows.length || !reviewNote.trim() || saving || uncertain} onClick={() => void submit('REJECT')}>驳回</button><button type="button" className={s.cancel} onClick={close} disabled={saving}>取消</button><button type="button" className={s.approve} disabled={READONLY || !canReview || request.status !== 'PENDING' || !rows.length || saving || uncertain} onClick={() => void submit('APPROVE')}>{saving ? '审核中…' : '通过变更'}</button></footer>
      </>}
    </section>
  </div>;
}
